import i18n from '@/utils/i18n';
import z from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { BusinessType } from '@/graphql/generatedTypes';

export const BUSINESS_TYPE_VALUES: Record<
  BusinessType,
  { value: BusinessType; getLabel: () => string }
> = {
  [BusinessType.SoleProprietorship]: {
    value: BusinessType.SoleProprietorship,
    getLabel: () => i18n.t('EventOrder:soleProprietorship'),
  },
  [BusinessType.Partnership]: {
    value: BusinessType.Partnership,
    getLabel: () => i18n.t('EventOrder:partnership'),
  },
  [BusinessType.LimitedPartnership]: {
    value: BusinessType.LimitedPartnership,
    getLabel: () => i18n.t('EventOrder:limitedPartnership'),
  },
  [BusinessType.PrivateLimitedCompany]: {
    value: BusinessType.PrivateLimitedCompany,
    getLabel: () => i18n.t('EventOrder:privateLimitedCompany'),
  },
  [BusinessType.PublicLimitedCompany]: {
    value: BusinessType.PublicLimitedCompany,
    getLabel: () => i18n.t('EventOrder:publicLimitedCompany'),
  },
  [BusinessType.NonProfitOrganization]: {
    value: BusinessType.NonProfitOrganization,
    getLabel: () => i18n.t('EventOrder:nonProfitOrganization'),
  },
  [BusinessType.GovernmentEntityPublicInstitution]: {
    value: BusinessType.GovernmentEntityPublicInstitution,
    getLabel: () => i18n.t('EventOrder:governmentEntityPublicInstitution'),
  },
  [BusinessType.Cooperative]: {
    value: BusinessType.Cooperative,
    getLabel: () => i18n.t('EventOrder:cooperative'),
  },
  [BusinessType.TrustFoundation]: {
    value: BusinessType.TrustFoundation,
    getLabel: () => i18n.t('EventOrder:trustOrFoundation'),
  },
} as const;

export const addressSchema = z.object({
  street: z.string().nonempty(),
  extra: z.string().optional(),
  zip: z.string().nonempty(),
  city: z.string().nonempty(),
  countryCode: z.string().nonempty(),
  state: z.string().optional(),
});

const commonFields = z.object({
  comment: z.string().optional(),
  firstName: z.string().nonempty(),
  lastName: z.string().nonempty(),
  companyName: z.string().nonempty(),
  email: z.email(),
  phoneNumber: z.string().refine(isValidPhoneNumber),
  address: addressSchema,
});

const nonSoleProprietorshipFields = z.object({
  corporateStructureInfo: z.string(),
  vatId: z.string().nonempty(),
});

export const orderFormSchema = z.discriminatedUnion('businessType', [
  // Sole Proprietorship
  commonFields.extend({
    businessType: z.literal(BusinessType.SoleProprietorship),

    dateOfBirth: z.date(),
  }),
  // Partnership (GbR, SNC)
  commonFields.extend({
    ...nonSoleProprietorshipFields.shape,
    businessType: z.literal(BusinessType.Partnership),
  }),
  // Limited Partnership
  commonFields.extend({
    ...nonSoleProprietorshipFields.shape,
    businessType: z.literal(BusinessType.LimitedPartnership),
  }),
  // Private Limited Company
  commonFields.extend({
    ...nonSoleProprietorshipFields.shape,
    businessType: z.literal(BusinessType.PrivateLimitedCompany),
  }),
  // Public Limited Company
  commonFields.extend({
    ...nonSoleProprietorshipFields.shape,
    businessType: z.literal(BusinessType.PublicLimitedCompany),
  }),
  // Non-Profit Organization
  commonFields.extend({
    ...nonSoleProprietorshipFields.shape,
    businessType: z.literal(BusinessType.NonProfitOrganization),
  }),
  // Government Entity or Public Institution
  commonFields.extend({
    ...nonSoleProprietorshipFields.shape,
    businessType: z.literal(BusinessType.GovernmentEntityPublicInstitution),
  }),
  // Cooperative
  commonFields.extend({
    ...nonSoleProprietorshipFields.shape,
    businessType: z.literal(BusinessType.Cooperative),
  }),
  // Trust or Foundation
  commonFields.extend({
    ...nonSoleProprietorshipFields.shape,
    businessType: z.literal(BusinessType.TrustFoundation),
  }),
]);

export type OrderFormValues = z.infer<typeof orderFormSchema>;

export const defaultFormValues: Partial<OrderFormValues> = {
  corporateStructureInfo: '',
  vatId: '',
  comment: '',
  address: {
    countryCode: 'DE',
    street: '',
    extra: '',
    zip: '',
    city: '',
  },
};
